'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen,
  Plus,
  Download,
  Loader2,
  AlertCircle,
  RefreshCw,
  Calendar,
  HelpCircle,
  Check,
  X,
} from 'lucide-react';
import { PDTrackerProps, PDActivity, PDStats, PDCycle } from './types';
import { getCRPOCycle, calculateStats, generateCSV, downloadCSV, formatDateRange } from './utils';
import { PDEducationBanner } from './PDEducationBanner';
import { PDProgressSection } from './PDProgressSection';
import { PDActivityTable } from './PDActivityTable';
import { AddPDActivityForm } from './AddPDActivityForm';

interface PDTrackerState {
  activities: PDActivity[];
  stats: PDStats | null;
  cycle: PDCycle | null;
  isLoading: boolean;
  error: string | null;
}

export function PDTracker({
  proId,
  crpoRegistrationDate,
  apiEndpoint = '/api/pd',
  profileApiEndpoint = '/api/profile',
  onActivityAdded,
  onActivityUpdated,
  onActivityDeleted,
  onRegistrationDateSaved,
}: PDTrackerProps) {
  const [state, setState] = useState<PDTrackerState>({
    activities: [],
    stats: null,
    cycle: null,
    isLoading: true,
    error: null,
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<PDActivity | null>(null);

  // Track if we should open the add form after loading completes (for new registrations)
  const openAddFormAfterLoad = useRef(false);

  // Registration date form state
  const [registrationDate, setRegistrationDate] = useState('');
  const [isSavingDate, setIsSavingDate] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [localRegistrationDate, setLocalRegistrationDate] = useState<string | null>(crpoRegistrationDate || null);

  // Update local registration date when prop changes (handles both truthy and null values)
  useEffect(() => {
    setLocalRegistrationDate(crpoRegistrationDate || null);
  }, [crpoRegistrationDate]);

  // Calculate cycle from registration date
  useEffect(() => {
    if (localRegistrationDate) {
      const regDate = new Date(localRegistrationDate);
      const cycle = getCRPOCycle(regDate);
      setState((prev) => ({ ...prev, cycle }));
    }
  }, [localRegistrationDate]);

  // Handle saving registration date
  const handleSaveRegistrationDate = async () => {
    if (!registrationDate) {
      setDateError('Please enter your CRPO registration date');
      return;
    }

    // Validate date is not in the future
    const selectedDate = new Date(registrationDate);
    if (selectedDate > new Date()) {
      setDateError('Registration date cannot be in the future');
      return;
    }

    setIsSavingDate(true);
    setDateError(null);

    try {
      const response = await fetch(profileApiEndpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          proId, // Include proId for impersonation support
          crpoRegistrationDate: registrationDate,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save registration date');
      }

      // Update local state
      setLocalRegistrationDate(registrationDate);
      onRegistrationDateSaved?.(registrationDate);

      // Flag to open the Add Activity form after loading completes
      openAddFormAfterLoad.current = true;
    } catch (err) {
      console.error('Error saving registration date:', err);
      setDateError(err instanceof Error ? err.message : 'Failed to save registration date');
    } finally {
      setIsSavingDate(false);
    }
  };

  // Fetch activities (uses combined endpoint to include supervision logs)
  const fetchActivities = useCallback(async () => {
    if (!proId) return;

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Use combined endpoint to get both manual PD activities and supervision logs
      const combinedEndpoint = `${apiEndpoint}/combined`;
      const response = await fetch(`${combinedEndpoint}?proId=${proId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch activities');
      }

      const activities = data.activities || [];

      // Calculate stats if we have a cycle
      let stats: PDStats | null = null;
      if (state.cycle) {
        stats = calculateStats(activities, state.cycle);
      }

      setState((prev) => ({
        ...prev,
        activities,
        stats,
        isLoading: false,
      }));
    } catch (err) {
      console.error('Error fetching PD activities:', err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load activities',
      }));
    }
  }, [proId, apiEndpoint, state.cycle]);

  // Recalculate stats when cycle changes
  useEffect(() => {
    if (state.cycle && state.activities.length > 0) {
      const stats = calculateStats(state.activities, state.cycle);
      setState((prev) => ({ ...prev, stats }));
    }
  }, [state.cycle, state.activities]);

  // Initial fetch
  useEffect(() => {
    if (proId && state.cycle) {
      fetchActivities();
    }
  }, [proId, state.cycle, fetchActivities]);

  // Open add form after loading completes (for new registrations)
  useEffect(() => {
    if (!state.isLoading && openAddFormAfterLoad.current) {
      openAddFormAfterLoad.current = false;
      setShowAddForm(true);
    }
  }, [state.isLoading]);

  // Handle export
  const handleExport = () => {
    const csv = generateCSV(state.activities);
    const date = new Date().toISOString().split('T')[0];
    downloadCSV(csv, `pd-activities-${date}.csv`);
  };

  // Handle activity added
  const handleActivityAdded = () => {
    setShowAddForm(false);
    fetchActivities();
    onActivityAdded?.();
  };

  // Handle activity edited
  const handleActivityEdited = () => {
    setEditingActivity(null);
    fetchActivities();
    onActivityUpdated?.();
  };

  // Handle activity deleted (only for manual activities, not supervision-sourced)
  const handleActivityDeleted = async (activityId: string) => {
    // Guard: Don't allow deleting supervision-sourced activities
    if (activityId.startsWith('supervision-')) {
      console.warn('Cannot delete supervision-sourced activities');
      return;
    }

    try {
      const response = await fetch(`${apiEndpoint}/${activityId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete activity');
      }

      fetchActivities();
      onActivityDeleted?.();
    } catch (err) {
      console.error('Error deleting activity:', err);
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to delete activity',
      }));
    }
  };

  // No registration date set - show input form
  if (!localRegistrationDate) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Professional Development Hours
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Track your CRPO PD requirements
              </p>
            </div>
          </div>
        </div>

        {/* Registration Date Input */}
        <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-center mb-6">
              <div className="p-3 bg-purple-100 rounded-full">
                <Calendar className="w-8 h-8 text-purple-600" />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              When did you register with CRPO?
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Your PD cycle is calculated based on your CRPO registration date.
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="registration-date" className="block text-sm font-medium text-gray-700 mb-1">
                  CRPO Registration Date
                </label>
                <input
                  type="date"
                  id="registration-date"
                  value={registrationDate}
                  onChange={(e) => {
                    setRegistrationDate(e.target.value);
                    setDateError(null);
                  }}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                {dateError && (
                  <p className="mt-1 text-sm text-red-600">{dateError}</p>
                )}
              </div>

              <button
                onClick={handleSaveRegistrationDate}
                disabled={isSavingDate || !registrationDate}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSavingDate ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Get Started
                  </>
                )}
              </button>
            </div>

            {/* Help text */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start gap-3">
                <HelpCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-600">
                  <p className="font-medium text-gray-700 mb-1">Where can I find this?</p>
                  <p>
                    Your registration date is on your CRPO certificate or in your{' '}
                    <a
                      href="https://www.crpo.ca/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-700 underline"
                    >
                      CRPO member portal
                    </a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (state.isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
        <p className="text-gray-600 mt-2">Loading PD activities...</p>
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8">
        <div className="flex items-center justify-center gap-2 mb-4">
          <AlertCircle className="w-6 h-6 text-red-500" />
          <p className="text-red-800 font-medium">{state.error}</p>
        </div>
        <button
          onClick={fetchActivities}
          className="flex items-center gap-2 mx-auto px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Professional Development Hours
              </h2>
              {state.cycle && (
                <p className="text-sm text-gray-600 mt-1">
                  Current cycle: {formatDateRange(state.cycle.start, state.cycle.end)}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {state.activities.length > 0 && (
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            )}
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Activity
            </button>
          </div>
        </div>
      </div>

      {/* Education Banner */}
      <PDEducationBanner
        stats={state.stats}
        cycle={state.cycle}
      />

      {/* Progress Section */}
      {state.stats && (
        <PDProgressSection stats={state.stats} />
      )}

      {/* Activities Table */}
      <PDActivityTable
        activities={state.activities}
        cycle={state.cycle}
        onDelete={handleActivityDeleted}
        onEdit={(activity) => setEditingActivity(activity)}
      />

      {/* Add Activity Form Modal */}
      {showAddForm && (
        <Modal
          title="Add PD Activity"
          onClose={() => setShowAddForm(false)}
          maxWidth="max-w-2xl"
        >
          <AddPDActivityForm
            proId={proId}
            apiEndpoint={apiEndpoint}
            crpoRegistrationDate={localRegistrationDate}
            onSuccess={handleActivityAdded}
            onCancel={() => setShowAddForm(false)}
          />
        </Modal>
      )}

      {/* Edit Activity Form Modal */}
      {editingActivity && (
        <Modal
          title="Edit Activity"
          onClose={() => setEditingActivity(null)}
          maxWidth="max-w-md"
        >
          <EditPDActivityForm
            activity={editingActivity}
            apiEndpoint={apiEndpoint}
            onSuccess={handleActivityEdited}
            onCancel={() => setEditingActivity(null)}
          />
        </Modal>
      )}
    </div>
  );
}

// Accessible Modal Component with focus trap
interface ModalProps {
  title: string;
  onClose: () => void;
  maxWidth?: string;
  children: React.ReactNode;
}

function Modal({ title, onClose, maxWidth = 'max-w-md', children }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = `modal-title-${title.toLowerCase().replace(/\s+/g, '-')}`;

  // Store previous focus and focus modal on mount
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus the first focusable element in the modal
    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements && focusableElements.length > 0) {
      focusableElements[0]?.focus();
    }

    // Restore focus on unmount
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Focus trap
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement?.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement?.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-xl shadow-xl ${maxWidth} w-full max-h-[90vh] overflow-y-auto`}
        onKeyDown={handleKeyDown}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 id={titleId} className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
}

// Edit Activity Form Component
interface EditPDActivityFormProps {
  activity: PDActivity;
  apiEndpoint: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function EditPDActivityForm({
  activity,
  apiEndpoint,
  onSuccess,
  onCancel,
}: EditPDActivityFormProps) {
  const [title, setTitle] = useState(activity.title);
  const [description, setDescription] = useState(activity.description || '');
  const [provider, setProvider] = useState(activity.provider || '');
  // The API returns activityDate as "YYYY-MM-DD" (Postgres `date` column);
  // slice protects against the legacy "YYYY-MM-DDT…" shape just in case.
  const [activityDate, setActivityDate] = useState(
    typeof activity.activityDate === 'string' ? activity.activityDate.slice(0, 10) : '',
  );
  const [durationHours, setDurationHours] = useState(Math.floor(activity.duration / 60));
  const [durationMinutes, setDurationMinutes] = useState(activity.duration % 60);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!activityDate) {
      setError('Activity date is required');
      return;
    }

    const duration = durationHours * 60 + durationMinutes;
    if (duration <= 0) {
      setError('Duration must be greater than 0');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${apiEndpoint}/${activity.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || null,
          provider: provider.trim() || null,
          activityDate,
          duration,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update activity');
      }

      onSuccess();
    } catch (err) {
      console.error('Error updating activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to update activity');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 mb-1">
          Title
        </label>
        <input
          id="edit-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          aria-required="true"
        />
      </div>

      <div>
        <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="edit-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
        />
      </div>

      <div>
        <label htmlFor="edit-provider" className="block text-sm font-medium text-gray-700 mb-1">
          Provider
        </label>
        <input
          id="edit-provider"
          type="text"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        />
      </div>

      <div>
        <label htmlFor="edit-activity-date" className="block text-sm font-medium text-gray-700 mb-1">
          Date <span className="text-red-500">*</span>
        </label>
        <input
          id="edit-activity-date"
          type="date"
          value={activityDate}
          onChange={(e) => setActivityDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          aria-required="true"
        />
      </div>

      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-1">
          Duration
        </legend>
        <div className="flex items-center gap-2">
          <input
            id="edit-hours"
            type="number"
            min="0"
            max="99"
            value={durationHours}
            onChange={(e) => setDurationHours(parseInt(e.target.value) || 0)}
            className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center"
            aria-label="Hours"
          />
          <span className="text-gray-600">h</span>
          <input
            id="edit-minutes"
            type="number"
            min="0"
            max="59"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 0)}
            className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center"
            aria-label="Minutes"
          />
          <span className="text-gray-600">m</span>
        </div>
      </fieldset>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
